import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";

type CalendlyQuestionData = {
  url?: string;
};

type Props = {
  data?: unknown;
  onDataChanged: (data: CalendlyQuestionData) => void;
};

const CALENDLY_HOST_SUFFIX = "calendly.com";

function normalizeCalendlyUrl(raw: string) {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { value: "", error: "Add your Calendly link to enable this step." };
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { value: trimmed, error: "Enter a valid URL." };
  }
  if (!["https:", "http:"].includes(url.protocol)) {
    return { value: trimmed, error: "Calendly links must start with http(s)." };
  }
  const host = url.hostname.toLowerCase();
  if (!host.endsWith(CALENDLY_HOST_SUFFIX)) {
    return { value: trimmed, error: "Use a calendly.com link." };
  }
  if (!url.pathname || url.pathname === "/") {
    return {
      value: trimmed,
      error: "Paste the full scheduling link, not just calendly.com.",
    };
  }
  return { value: url.toString(), error: null };
}

const CalendlyType: React.FC<Props> = ({ data, onDataChanged }) => {
  const value =
    typeof data === "object" && data ? (data as CalendlyQuestionData).url ?? "" : "";
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!value.trim()) {
      setError("Add your Calendly link to enable this step.");
      return;
    }
    setError(null);
  }, [value]);

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Calendly link</p>
      <Input
        value={value}
        onChange={(event) => {
          setError(null);
          onDataChanged({ url: event.target.value });
        }}
        onBlur={() => {
          const normalized = normalizeCalendlyUrl(value);
          setError(normalized.error);
          if (!normalized.error && normalized.value !== value) {
            onDataChanged({ url: normalized.value });
          }
        }}
        placeholder="https://calendly.com/your-name/intro-call"
      />
      <p className="text-xs text-muted-foreground">
        Paste your Calendly scheduling URL so leads can book directly in the form.
      </p>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
};

export default CalendlyType;
